import React, { useState } from "react";
import styled from "styled-components";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import { Input, Button, message as antdMessage } from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { device } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    // Reset error state
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsLoading(true);

      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      const response = await fetch(`${apiUrl}/marketing/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          source: "homepage",
        }),
      });

      const result = await response.json();

      if (response.ok || result.success) {
        setIsSuccess(true);
        setEmail("");
        setFirstName("");

        // Track successful subscription
        trackEvent("newsletter_signup", {
          source: "homepage",
          email: email.trim(),
        });

        // Show success message
        antdMessage.success("Thanks! We'll keep you updated.");
      } else {
        // Handle API error
        setError(
          result.error ||
            result.message ||
            "Subscription failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      setError("Unable to connect. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  if (isSuccess) {
    return (
      <NewsletterContainer>
        <SuccessState>
          <SuccessIcon>
            <CheckCircleOutlined />
          </SuccessIcon>
          <SuccessTitle>You're all set!</SuccessTitle>
          <SuccessMessage>
            We'll shoot you updates on new features and show you what Leo AI can
            do. You can unsubscribe anytime.
          </SuccessMessage>
        </SuccessState>
      </NewsletterContainer>
    );
  }

  return (
    <NewsletterContainer id="stay-in-the-loop">
      <NewsletterContent>
        <NewsletterHeader>
          <NewsletterIcon>
            <MailOutlined />
          </NewsletterIcon>
          <NewsletterTitle>Stay in the Loop</NewsletterTitle>
          <NewsletterSubtitle>
            Get updates on new LandlordOps features, product improvements, and
            see what Leo AI can do. Perfect if you're not quite ready to sign up
            yet.
          </NewsletterSubtitle>
        </NewsletterHeader>

        <SignupForm onSubmit={handleSubscribe}>
          <FormRow>
            <FormGroup $half>
              <StyledInput
                type="text"
                placeholder="First name (optional)"
                value={firstName}
                onChange={handleFirstNameChange}
                disabled={isLoading}
                size="large"
              />
            </FormGroup>
            <FormGroup $half>
              <StyledInput
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                required
                size="large"
                status={error ? "error" : ""}
              />
            </FormGroup>
          </FormRow>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton
            type="primary"
            htmlType="submit"
            size="large"
            disabled={isLoading || !email.trim()}
            icon={
              isLoading ? (
                <LoadingOutlined
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              ) : (
                <MailOutlined style={{ filter: "brightness(0) invert(1)" }} />
              )
            }
          >
            {isLoading ? "Subscribing..." : "Subscribe to Updates"}
          </SubmitButton>

          <PrivacyNote>
            We respect your privacy. Unsubscribe anytime. No spam, just updates
            on LandlordOps.
          </PrivacyNote>
        </SignupForm>
      </NewsletterContent>
    </NewsletterContainer>
  );
};

const NewsletterContainer = styled.section`
  padding: 5rem 2rem;
  /* Starts clean white so it does not blend with the sage Scale OS band above */
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 38%, #f4faf4 100%);
  border-top: 1px solid rgba(44, 95, 45, 0.14);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 50% 115%,
      rgba(74, 124, 89, 0.07) 0%,
      transparent 52%
    );
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const NewsletterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const NewsletterHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media ${device.mobile} {
    margin-bottom: 2rem;
  }
`;

const NewsletterIcon = styled.div`
  font-size: 3rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;

  .anticon {
    font-size: 3rem;
  }

  @media ${device.mobile} {
    font-size: 2.5rem;
    margin-bottom: 1rem;

    .anticon {
      font-size: 2.5rem;
    }
  }
`;

const NewsletterTitle = styled.h2`
  ${marketingSectionHeading}
  color: var(--color-text);
  margin-bottom: 1rem;
  text-align: center;

  @media ${device.mobile} {
    margin-bottom: 0.75rem;
  }
`;

const NewsletterSubtitle = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  max-width: 700px;
  margin: 0 auto;

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const SignupForm = styled.form`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    padding: 1.75rem 1.25rem;
    border-radius: 12px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  flex: ${(props) => (props.$half ? "1" : "auto")};
  width: ${(props) => (props.$half ? "auto" : "100%")};

  @media ${device.mobile} {
    width: 100%;
  }
`;

const StyledInput = styled(Input)`
  font-family: var(--font-secondary);
  font-size: 1rem;

  &.ant-input-lg {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 2px solid rgba(44, 95, 45, 0.2);

    &:hover,
    &:focus {
      border-color: var(--color-primary);
    }
  }

  @media ${device.mobile} {
    &.ant-input-lg {
      font-size: 0.95rem;
      padding: 0.65rem 0.875rem;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #fee2e2;
  border-radius: 8px;
  border: 1px solid #fecaca;

  @media ${device.mobile} {
    font-size: 0.85rem;
    padding: 0.625rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: auto;
  padding: 0.875rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  border-radius: 999px;
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  border: none;
  box-shadow: 0 4px 18px rgba(44, 95, 45, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.3);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 1.1rem;
  }

  @media ${device.mobile} {
    font-size: 1rem;
    padding: 0.75rem 1.25rem;

    .anticon {
      font-size: 1rem;
    }
  }
`;

const PrivacyNote = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.875rem;
  color: var(--color-text-light);
  text-align: center;
  margin: 1.25rem 0 0 0;
  line-height: 1.5;

  @media ${device.mobile} {
    font-size: 0.8rem;
    margin-top: 1rem;
  }
`;

const SuccessState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 2px solid var(--color-primary);

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;

  .anticon {
    font-size: 4rem;
  }

  @media ${device.mobile} {
    font-size: 3rem;
    margin-bottom: 1rem;

    .anticon {
      font-size: 3rem;
    }
  }
`;

const SuccessTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family:
    "Poppins",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media ${device.mobile} {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  max-width: 600px;
  margin: 0 auto;

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

export default NewsletterSignup;
